"use client";

import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";

// Surfaced when the user manually picks an MCP source from the plus-menu and
// the validation roundtrip resolves to a terminal/error state. Two modes:
//   paywalled — 402 from upstream. Copy points the user at sales; no detail.
//   error     — generic transient or connection failure. Shows the error
//               message for debugging.
//
// Note: this dialog never opens on startup auto-connect — it would be too
// disruptive. The chip's badge (lock icon for paywalled, red dot for error)
// is the only signal there. The dialog only fires on explicit user pick.
export const SourceUnavailableDialog: FC = () => {
  const { t } = useTranslation();
  const { sourceUnavailable, closeSourceUnavailable, allServers } = useMcpServers();

  if (!sourceUnavailable.open) return null;

  const serverLabel = allServers.find(s => s.name === sourceUnavailable.serverName)
    ? t(`${sourceUnavailable.serverName}.label`)
    : sourceUnavailable.serverName;

  const body = sourceUnavailable.mode === "paywalled"
    ? t("composer.mcp.sourceUnavailable.paywalledMessage")
    : t("composer.mcp.sourceUnavailable.errorMessage", { error: sourceUnavailable.errorDetail ?? "" });

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) closeSourceUnavailable(); }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t("composer.mcp.sourceUnavailable.title", { name: serverLabel })}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="default" onClick={closeSourceUnavailable}>
            {t("composer.mcp.sourceUnavailable.ok")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
